import Cookies from 'js-cookie';
import * as Sentry from '@sentry/react';
import type { NavigateFunction } from 'react-router-dom';
import { isTrue } from './strings';
import { getDeepLinkActions, removeDeepLinkActions, removeUser, saveDeepLinkActions } from '../localStorageAPI';
import envConfig from '../config/currentEnv';
import { paths } from '../navigation/routes/paths';
import appConfig from '../config/appConfig';
import { APP_ANDROID_URL, APP_IOS_URL, WEBSITE_SELF_SERVICE_URL } from './constants';
import { isDeviceAndroid, isWebView, isWebViewAndroid } from './webview';
import { DeepLink } from '../core/apiTypes';

export const redirectToAuth = (msg = '', data = {}, deleteUserCache = true): void => {
    if (deleteUserCache) {
        removeUser();
    }

    const deepLinkAction = getDeepLinkActions()?.[0];

    // console.log('=======================');
    // console.log({ deepLinkAction });
    // console.log('=======================');

    if (deepLinkAction?.action === 'selfService') {
        window.location.href = `${envConfig.currentEnv.authURL}/signup?selfservice=1`;
    } else if (deepLinkAction?.action === 'book' && deepLinkAction?.groupKey) {
        const { groupKey, exist, sfirstname, slastname, smail, sphone, app } = deepLinkAction;
        const redirection = isTrue(exist)
            ? 'home'
            : `data?action=signup&firstname=${sfirstname}&lastname=${slastname}&email=${smail}&phone=${sphone}&groupKey=${groupKey}${app ? `&app=${app}` : ''}`;

        removeDeepLinkActions('book');
        const keysToRemove: (keyof DeepLink)[] = ['sfirstname', 'slastname', 'sphone', 'smail', 'groupKey', 'exist'];

        const newDeeplink = Object.fromEntries(
            Object.entries(deepLinkAction).filter(([key]) => !keysToRemove.includes(key as keyof DeepLink)),
        );
        console.log(JSON.stringify(newDeeplink));
        saveDeepLinkActions(newDeeplink as DeepLink);

        window.location.href = `${envConfig.currentEnv.authURL}/${redirection}`;
    } else {
        if (appConfig.useSentry && msg) {
            Sentry.captureMessage(
                `Message: ${msg} with data: ${data ? JSON.stringify(data) : '-'}`,
                Sentry.Severity.Debug,
            );
        }
        // Deletion only works when path AND domain match how the cookie was set (path '/',
        // domain-scoped by ID). js-cookie drops falsy attributes, so the previous `path: ''`
        // fell back to the current page path: it left the real cookie alive and added an empty
        // duplicate named `token` on that path. Clear both the scoped and host-only variants.
        Cookies.remove('token', { path: '/', domain: envConfig.currentEnv.domain });
        Cookies.remove('token', { path: '/' });
        window.location.href = `${envConfig.currentEnv.authURL}${
            window.location.pathname === paths.concur
                ? `${window.location.pathname}${window.location.search}`
                : msg
                  ? `/home?reason=${encodeURI(msg)}`
                  : ''
        }`;
    }
};

export const redirectToBackOffice = (deleteUserCache = true): void => {
    if (deleteUserCache) {
        removeUser();
    }
    window.location.href = envConfig.currentEnv.boURL;
};

export const redirectToCustomFormula = (): void => {
    window.location.href = WEBSITE_SELF_SERVICE_URL;
};

export const redirectToStore = (): void => {
    let appUrl = APP_IOS_URL;
    if (isWebViewAndroid() || isDeviceAndroid()) {
        appUrl = APP_ANDROID_URL;
    }
    window.location.href = appUrl;
};

/**
 * allow extern or intern navigation
 * @param navigate NavigateFunction from react-router-dom
 * @param path externe url or intern path
 * @param newTab open the external link in a new tab
 */
export const navigateTo = (navigate: NavigateFunction, path: string, newTab = true): void => {
    if (isExternalUrl(path)) {
        // Native webview doesn't support window.open so we let the app handle the external urls redirection
        if (newTab && !isWebView()) {
            window.open(path, '_blank');
        } else {
            window.location.href = path;
        }
    } else {
        navigate(path);
    }
};

export const isExternalUrl = (path: string): boolean => {
    return path.startsWith('http');
};
